<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(defineProps<{
  rating?: number | null
  count?: number | null
  size?: number          // star font-size in px
  showCount?: boolean
  numbersOnly?: boolean  // render just the numeric rating, no star row
}>(), { size: 13, showCount: true, numbersOnly: false })

const value = computed(() => Number(props.rating) || 0)
const pct = computed(() => Math.max(0, Math.min(100, (value.value / 5) * 100)))
</script>

<template>
  <span class="star-rating" :style="{ fontSize: size + 'px' }">
    <template v-if="value > 0">
      <span v-if="!numbersOnly" class="sr-stars" :style="{ '--pct': pct + '%' }">
        <span class="sr-bg">★★★★★</span>
        <span class="sr-fg">★★★★★</span>
      </span>
      <span class="sr-num" :class="{ 'sr-num-solo': numbersOnly }">{{ value.toFixed(1) }}</span>
      <span v-if="showCount && count" class="sr-count">({{ Number(count).toLocaleString() }})</span>
    </template>
    <!-- Unrated: render nothing — an "Unrated" tag on big suppliers reads as
         "nobody uses this platform" rather than as missing data. -->
  </span>
</template>

<style scoped>
.star-rating { display: inline-flex; align-items: center; gap: 5px; line-height: 1; white-space: nowrap; }
.sr-stars { position: relative; display: inline-block; }
.sr-bg { color: var(--cc-border); letter-spacing: 1px; }
.sr-fg {
  position: absolute; left: 0; top: 0;
  width: var(--pct); overflow: hidden;
  color: var(--cc-warn); letter-spacing: 1px;
  white-space: nowrap;
}
.sr-num {
  font-family: var(--font-mono); font-variant-numeric: tabular-nums;
  font-weight: 700; color: var(--cc-text); font-size: 0.85em;
}
.sr-num-solo { font-size: 1em; }
.sr-count { color: var(--cc-text-3); font-size: 0.78em; }
.sr-none { color: var(--cc-text-3); font-size: 0.82em; font-style: italic; }
</style>
